import React, { useState, useRef, useEffect } from "react";
import { API_URL } from "@/env";
import { login } from "@/services/auth";
import useUserData from "@/hooks/useUserData";
import { registerWithEmail } from "@/services/auth";
import { showCustomToast } from "./CustomToast";
import { setCookie } from "@/lib/cookies";

interface CreateAccountProps {
  setShowRegister: React.Dispatch<React.SetStateAction<boolean>>;
  workspaceId?: string;
  workspaceName?: string;
}

type ViewType = "initial" | "email" | "login";

const useNavigate = () => {
  return (path: string) => {
    console.log("Navigate to:", path);
    // Your actual navigation here
  };
};

const GoogleIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.1712 8.36791H17.5V8.33333H10V11.6667H14.7095C14.0225 13.6071 12.1762 15 10 15C7.23875 15 5 12.7612 5 10C5 7.23875 7.23875 5 10 5C11.2745 5 12.4345 5.48083 13.317 6.26625L15.6745 3.90875C14.1858 2.52208 12.195 1.66667 10 1.66667C5.39833 1.66667 1.66667 5.39833 1.66667 10C1.66667 14.6017 5.39833 18.3333 10 18.3333C14.6017 18.3333 18.3333 14.6017 18.3333 10C18.3333 9.44125 18.2758 8.89583 18.1712 8.36791Z"
      fill="#FFC107"
    />
    <path
      d="M2.6275 6.12125L5.36541 8.12958C6.10625 6.295 7.90041 5 10 5C11.2745 5 12.4345 5.48083 13.317 6.26625L15.6745 3.90875C14.1858 2.52208 12.195 1.66667 10 1.66667C6.79917 1.66667 4.02333 3.47375 2.6275 6.12125Z"
      fill="#FF3D00"
    />
    <path
      d="M10 18.3333C12.1525 18.3333 14.1083 17.5096 15.5871 16.17L13.0083 13.9875C12.1432 14.6452 11.0865 15.0009 10 15C7.8325 15 5.99208 13.6179 5.29875 11.6892L2.58167 13.8158C3.96042 16.5346 6.76083 18.3333 10 18.3333Z"
      fill="#4CAF50"
    />
    <path
      d="M18.1712 8.36791H17.5V8.33333H10V11.6667H14.7095C14.3809 12.5902 13.7889 13.3972 13.0067 13.9875L13.0083 13.9862L15.5871 16.1687C15.4046 16.3354 18.3333 14.1667 18.3333 10C18.3333 9.44125 18.2758 8.89583 18.1712 8.36791Z"
      fill="#1976D2"
    />
  </svg>
);

const LogoIcon: React.FC = () => (
  <svg
    width="38"
    height="35"
    viewBox="0 0 38 35"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="13.1339" cy="7.08799" r="2.71299" fill="#EEEEEE" />
    <circle cx="24.8658" cy="7.08799" r="2.71299" fill="#EEEEEE" />
    <circle cx="7.26816" cy="17.5001" r="2.71299" fill="#EEEEEE" />
    <circle cx="19.0001" cy="17.5001" r="2.71299" fill="#EEEEEE" />
    <circle cx="30.732" cy="17.5001" r="2.71299" fill="#EEEEEE" />
    <circle cx="13.1339" cy="27.9122" r="2.71299" fill="#EEEEEE" />
    <circle cx="24.8658" cy="27.9122" r="2.71299" fill="#EEEEEE" />
  </svg>
);

interface ArrowIconProps {
  opacity?: number;
}

const ArrowIcon: React.FC<ArrowIconProps> = ({ opacity = 0.24 }) => (
  <svg
    width="23"
    height="23"
    viewBox="0 0 23 23"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.91675 11.5002C1.91675 11.7091 1.91866 11.9142 1.92154 12.1154L1.93783 12.7057L1.95029 12.9923L1.98287 13.5472L2.02696 14.0771C2.47162 18.6627 4.3375 20.5286 8.92312 20.9733L9.45308 21.0174L10.008 21.05C10.1019 21.0547 10.1977 21.0595 10.2945 21.0624L10.8848 21.0787L11.1896 21.0825L11.5001 21.0835C11.709 21.0835 11.9141 21.0816 12.1153 21.0787L12.7057 21.0624L12.9922 21.05L13.5471 21.0174L14.077 20.9733C18.6627 20.5286 20.5285 18.6627 20.9732 14.0771L21.0173 13.5472L21.0499 12.9923C21.0547 12.8984 21.0595 12.8025 21.0623 12.7057L21.0786 12.1154L21.0834 11.5002L21.0786 10.8849L21.0623 10.2946L21.0499 10.008L21.0173 9.45316L20.9732 8.9232C20.5285 4.33758 18.6627 2.4717 14.077 2.02704L13.5471 1.98296L12.9922 1.95037C12.8983 1.94558 12.8025 1.94079 12.7057 1.93791L12.1153 1.92162L11.5001 1.91683L10.8848 1.92162L10.2945 1.93791L10.008 1.95037L9.45308 1.98296L8.92312 2.02704C4.3375 2.4717 2.47162 4.33758 2.02696 8.9232L1.98287 9.45316L1.95029 10.008C1.94563 10.1035 1.94147 10.199 1.93783 10.2946L1.92154 10.8849L1.91771 11.1897L1.91675 11.5002ZM9.86421 15.0527C9.6992 14.8877 9.60008 14.6681 9.58544 14.4352C9.57079 14.2023 9.64163 13.9721 9.78466 13.7877L9.86421 13.6976L12.0607 11.5002L9.86421 9.3027C9.6992 9.13769 9.60008 8.91813 9.58544 8.68523C9.57079 8.45233 9.64163 8.22209 9.78466 8.0377L9.86421 7.94762C10.0292 7.78262 10.2488 7.6835 10.4817 7.66885C10.7146 7.65421 10.9448 7.72505 11.1292 7.86808L11.2193 7.94762L14.0943 10.8226C14.2593 10.9876 14.3584 11.2072 14.3731 11.4401C14.3877 11.673 14.3169 11.9032 14.1738 12.0876L14.0943 12.1777L11.2193 15.0527C11.0396 15.2324 10.7959 15.3333 10.5417 15.3333C10.2876 15.3333 10.0439 15.2324 9.86421 15.0527Z"
      fill="white"
      fillOpacity={opacity}
    />
  </svg>
);

const validateEmail = (email: string): boolean => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email.toLowerCase());
};

const CreateAccount: React.FC<CreateAccountProps> = ({
  setShowRegister,
  workspaceId,
  workspaceName,
}) => {
  const formRef = useRef<HTMLDivElement>(null);
  const { setUser } = useUserData();
  const navigate = useNavigate();

  const [view, setView] = useState<ViewType>("initial");
  const [email, setEmail] = useState<string>("");
  const [loginEmail, setLoginEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const sendJoinRequest = async (token: string) => {
    if (!workspaceId) return;

    try {
      await fetch(`${API_URL}/workspaces/${workspaceId}/join-request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      showCustomToast("Join request sent successfully!");
    } catch (error) {
      console.error("Error sending join request:", error);
    }
  };

  const handleGoogleLogin = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError("");

      const currentUrl = window.location.href;

      // Build the URL with workspaceId if present
      let url = `${API_URL}/auth/google/login?no_redirect=true&returnUrl=${encodeURIComponent(
        currentUrl
      )}`;

      if (workspaceId) {
        url += `&workspaceId=${encodeURIComponent(workspaceId)}`;
      }

      const res = await fetch(url);
      const data = await res.json();

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Google login error:", error);
      setError("Authentication failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailRegister = async (
    e?: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    if (e) {
      e.preventDefault();
    }
    setError("");

    if (!email.trim()) {
      setError("Email is required.");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setIsLoading(true);

      if (workspaceId) {
        const response = await fetch(`${API_URL}/auth/signup/email-only`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            workspaceId: workspaceId, // Changed from 'website' to 'workspaceId'
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          if (data.message === "User already exists") {
            setError("User already exists!");
          } else {
            setError(data.message || "Failed to create account");
          }
          return;
        }

        if (data?.access_token && data?.user) {
          setCookie("token", data.access_token);
          setUser(data.user);

          await sendJoinRequest(data.access_token);

          setShowRegister(false);
          showCustomToast(
            "Account created! Check your email for your password.",
            "check"
          );

          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      } else {
        const data = await registerWithEmail(email);

        if (data?.message === "User already exists") {
          setError("User already exists!");
          return;
        }

        if (data?.access_token && data?.user) {
          setUser(data.user);
          setShowRegister(false);
          showCustomToast("Logged in successfully", "check");
        }
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError("Try a different email!");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (
    e?: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    if (e) {
      e.preventDefault();
    }
    setError("");

    if (!loginEmail.trim()) {
      setError("Email is required.");
      return;
    }

    if (!validateEmail(loginEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!password.trim()) {
      setError("Password is required.");
      return;
    }

    if (password.length < 4) {
      setError("Password must be at least 4 characters long.");
      return;
    }

    try {
      setIsLoading(true);
      const data = await login(loginEmail, password);

      if (data?.access_token && data?.user) {
        setUser(data.user);

        if (workspaceId) {
          await sendJoinRequest(data.access_token);
        }

        setShowRegister(false);
        // showCustomToast("Logged in successfully", "check");

        if (workspaceId) {
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("Invalid credentials or an error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      setShowRegister(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setShowRegister(false);
      }
      if (e.key === "Enter" && view === "email" && email.trim()) {
        handleEmailRegister();
      }
      if (
        e.key === "Enter" &&
        view === "login" &&
        loginEmail.trim() &&
        password.trim()
      ) {
        handleLogin();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [email, loginEmail, password, view, setShowRegister]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");

    if (token) {
      const pendingWorkspace = localStorage.getItem("pendingWorkspaceJoin");

      if (pendingWorkspace) {
        const workspaceData = JSON.parse(pendingWorkspace);
        sendJoinRequest(token);
        localStorage.removeItem("pendingWorkspaceJoin");
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }
    }
  }, []);

  const getTitle = () => {
    if (workspaceId) {
      return workspaceName ? `Join ${workspaceName}` : "Join Workspace";
    }
    return view === "login"
      ? "Sign in to leave a comment"
      : "Sign-up to leave a comment";
  };

  return (
    <div
      className="fixed top-0 left-0 w-screen h-screen z-[100000001] flex items-center justify-center"
      style={{
        background: "rgba(0, 0, 0, 0.72)",
        backdropFilter: "blur(10px)",
        fontFamily: "'Onest', sans-serif",
      }}
      onClick={handleBackdropClick}
    >
      <div className="max-w-[320px] w-full" ref={formRef}>
        <div className="text-center flex flex-col items-center">
          <LogoIcon />
          <h2 className="text-white text-[20px] leading-[28px] font-semibold mt-5 mb-[34px] max-w-[218px]">
            {getTitle()}
          </h2>
        </div>

        {view === "initial" && (
          <div className="flex flex-col gap-1.5">
            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="flex items-center justify-center gap-2 bg-[#EEEEEE] text-[#0F0F0F] text-sm leading-5 h-9 rounded-xl w-full font-medium transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <GoogleIcon />
              Continue with Google
            </button>
            <button
              onClick={() => {
                setView("email");
                setError("");
              }}
              className="flex items-center justify-center bg-white/[0.08] text-white text-sm leading-5 h-9 rounded-xl w-full font-medium transition-all hover:bg-white/[0.12]"
            >
              Continue with Email
            </button>
          </div>
        )}

        {view === "email" && (
          <div>
            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="flex items-center justify-center gap-2 bg-[#EEEEEE] text-[#0F0F0F] text-sm leading-5 h-9 rounded-xl w-full font-medium transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed mb-1.5"
            >
              <GoogleIcon />
              Continue with Google
            </button>

            <div className="relative w-full">
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                placeholder="Type your Email"
                autoFocus
                className="h-9 border border-white/10 text-sm leading-5 px-4 pr-10 text-white bg-white/[0.08] rounded-xl w-full transition-all focus:outline-none focus:border-[#eee] focus:bg-white/10 placeholder:text-white/[0.32]"
              />
              <button
                onClick={handleEmailRegister}
                disabled={isLoading || !email.trim()}
                className="absolute top-1/2 -translate-y-1/2 right-[7px] bg-transparent border-0 w-[23px] h-[23px] cursor-pointer transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <ArrowIcon opacity={email.trim() ? 1 : 0.24} />
              </button>
            </div>

            {error && <p className="text-red-400 mt-2 text-sm">{error}</p>}
          </div>
        )}

        {view === "login" && (
          <div>
            <div className="flex flex-col gap-2">
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => {
                  setLoginEmail(e.target.value);
                  setError("");
                }}
                placeholder="Email"
                autoFocus
                className="h-9 border border-white/10 text-sm leading-5 px-4 text-white bg-white/[0.08] rounded-xl w-full transition-all focus:outline-none focus:border-[#eee] focus:bg-white/10 placeholder:text-white/[0.32]"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                placeholder="Password"
                className="h-9 border border-white/10 text-sm leading-5 px-4 text-white bg-white/[0.08] rounded-xl w-full transition-all focus:outline-none focus:border-[#eee] focus:bg-white/10 placeholder:text-white/[0.32]"
              />

              {error && <p className="text-red-400 text-sm">{error}</p>}

              <button
                onClick={handleLogin}
                disabled={isLoading}
                className="flex items-center justify-center bg-[#eee] text-[#0F0F0F] text-sm leading-5 h-9 rounded-xl w-full font-medium transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed mt-3"
              >
                {isLoading ? "Signing in..." : "Sign In"}
              </button>
            </div>

            <div className="flex items-center justify-center gap-2 my-5 text-xs leading-5 font-medium text-white/[0.32]">
              <div className="h-px w-full bg-white/[0.16]" />
              OR
              <div className="h-px w-full bg-white/[0.16]" />
            </div>

            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="flex items-center justify-center gap-2 bg-[#EEEEEE] text-[#0F0F0F] text-sm leading-5 h-9 rounded-xl w-full font-medium transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <GoogleIcon />
              Continue with Google
            </button>
          </div>
        )}

        <div className="text-center text-white/[0.32] text-xs leading-5 mt-5 font-medium">
          {view !== "login" ? (
            <>
              <p className="my-2.5">
                By signing up, you agree to our{" "}
                <a
                  target="_blank"
                  href="https://usetool.bar/terms-and-conditions"
                  className="underline"
                  rel="noopener noreferrer"
                >
                  Terms & Conditions
                </a>{" "}
                and{" "}
                <a
                  target="_blank"
                  href="https://usetool.bar/privacy-policy"
                  className="underline"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </p>
              <p className="my-2.5">
                Already have an account?{" "}
                <button
                  onClick={() => {
                    setView("login");
                    setError("");
                  }}
                  className="text-white cursor-pointer bg-transparent border-0 p-0 text-xs"
                >
                  Log in
                </button>
              </p>
            </>
          ) : (
            <p className="my-2.5">
              Don't have an account?{" "}
              <button
                onClick={() => {
                  setView("initial");
                  setError("");
                }}
                className="text-white cursor-pointer bg-transparent border-0 p-0 text-xs"
              >
                Sign up
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateAccount;
